import { useMemo } from 'react';
import { toast } from 'sonner';
import { NameDialog } from '@reflex/ui/components/shared/NameDialog';
import { useUpdateSnapshot } from '@/hooks/snapshots/useSnapshots';
import type { Snapshot } from '@/lib/types';

interface RenameSnapshotDialogProps {
  snapshot: Snapshot;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edits a snapshot's name and description. The server re-applies the org
 * name prefix and re-sends the existing Runloop metadata, so org scoping
 * survives the update.
 */
export function RenameSnapshotDialog({ snapshot, open, onOpenChange }: RenameSnapshotDialogProps) {
  const updateSnapshot = useUpdateSnapshot();
  const values = useMemo(
    () => ({ name: snapshot.name, description: snapshot.commitMessage ?? '' }),
    [snapshot.name, snapshot.commitMessage],
  );

  return (
    <NameDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit snapshot"
      description="Rename the snapshot or update its description. Existing launches keep working."
      nameLabel="Snapshot name"
      descriptionLabel="Snapshot description"
      descriptionPlaceholder="What state does this capture?"
      nameMax={100}
      descriptionMax={1000}
      submitLabel="Save changes"
      isPending={updateSnapshot.isPending}
      values={values}
      onSubmit={(data) => {
        updateSnapshot.mutate(
          {
            id: snapshot.id,
            data: {
              name: data.name,
              ...(data.description?.trim() ? { commitMessage: data.description.trim() } : {}),
            },
          },
          {
            onSuccess: () => {
              toast.success('Snapshot updated');
              onOpenChange(false);
            },
            onError: (err: Error) =>
              toast.error('Failed to update snapshot', { description: err.message }),
          },
        );
      }}
    />
  );
}
